import { UIElement } from "./ui_element.js";
import { UITextElement } from "./ui_text_element.js";
import { UIElementHolder } from "./ui_element_holder.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { SupportedLanguages } from "../../localization/supported_languages.js";

export class UIAdaptiveElementsByTextHorisontal extends UIElement
{
  private readonly ui_elements: UIElement[];
  private readonly gap: number;
  private gaps: number[] | undefined;

  private readonly text_element_indices: Set<number>;
  private readonly text_elements: readonly UITextElement[];
  private elements_holder: UIElementHolder | null = null;
  private onLanguageChange: ( ( lang: SupportedLanguages ) => void ) | null = null;
  private total_width: number = 0;
  private recalculate_animation_id: number | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    min_x: number,
    min_y: number,
    max_width: number,
    max_height: number,
    ui_elements?: UIElement[],
    gap?: number,
    gaps?: number[]
  )
  {
    super( ctx, min_x, min_y, max_width, max_height, false, false );
    this.ui_elements = ui_elements ?? [];
    this.gap = gap ?? 0;
    this.gaps = gaps;

    const { indices, elements } = this.findTextElementIndices();
    this.text_element_indices = indices;
    this.text_elements = elements;

    if ( this.text_elements.length > 0 )
    {
      this.onLanguageChange = () =>
      {
        if ( this.recalculate_animation_id != null )
          cancelAnimationFrame( this.recalculate_animation_id );
        this.recalculate_animation_id = requestAnimationFrame( () =>
        {
          this.recalculate_animation_id = null;
          this.recalculate();
        } );
      };
      LocalizationManager.subscribe( this.onLanguageChange );
    }

    this.createAdaptiveElementsByWidth();
  }

  private findTextElementIndices(): { indices: Set<number>; elements: readonly UITextElement[] }
  {
    const indices = new Set<number>();
    const elements: UITextElement[] = [];

    for ( let i = 0; i < this.ui_elements.length; i++ )
    {
      const el = this.ui_elements[i];
      if ( el instanceof UITextElement )
      {
        indices.add( i );
        elements.push( el );
      }
    }

    return { indices, elements: Object.freeze( elements ) };
  }

  private ensureGapsInitialized(): void
  {
    if ( this.gaps !== undefined )
      return;
    if ( this.ui_elements.length === 0 )
      return;

    const n = this.ui_elements.length + 2;
    this.gaps = [];
    for ( let i = 0; i < n; i++ )
    {
      this.gaps[i] = ( i === 0 || i === n - 1 ) ? 0 : this.gap;
    }
  }

  private getGapAt( index: number ): number
  {
    this.ensureGapsInitialized();
    return this.gaps?.[index] ?? 0;
  }

  private getGapsWidth(): number
  {
    this.ensureGapsInitialized();
    if ( !this.gaps || this.ui_elements.length === 0 )
      return 0;

    let width = 0;
    for ( let i = 0; i < this.ui_elements.length; i++ )
      width += this.getGapAt( i );
    return width;
  }

  private computeElementWidth( index: number ): number
  {
    const el = this.ui_elements[index];
    if ( !el )
      return 0;

    if ( this.text_element_indices.has( index ) )
    {
      const text_el = el as UITextElement;
      text_el.setNaturalTextWidth( true );
      return text_el.getNaturalTextWidthAtBaseFontSize() ?? 0;
    }
    return el.getRect().getW();
  }

  private createAdaptiveElementsByWidth(): void
  {
    if ( this.ui_elements.length === 0 )
      return;

    this.ensureGapsInitialized();

    const element_widths: number[] = [];
    let elements_width = 0;
    for ( let i = 0; i < this.ui_elements.length; i++ )
    {
      const w = this.computeElementWidth( i );
      element_widths.push( w );
      elements_width += w;
    }

    const gaps_width = this.getGapsWidth();
    const max_available = this.rect.getW();
    const available_for_elements = max_available - gaps_width;

    if ( elements_width > available_for_elements && this.text_element_indices.size > 0 )
    {
      const excess = elements_width - available_for_elements;
      const reduction_per_text = excess / this.text_element_indices.size;
      for ( let i = 0; i < this.ui_elements.length; i++ )
      {
        if ( this.text_element_indices.has( i ) )
        {
          const new_w = Math.max( 0, element_widths[i] - reduction_per_text );
          element_widths[i] = new_w;
        }
      }
      elements_width = element_widths.reduce( ( sum, w ) => sum + w, 0 );
    }

    this.total_width = Math.min( elements_width + gaps_width, max_available );
    const holder_x = ( this.rect.getW() - this.total_width ) / 2;
    const holder_h = this.rect.getH();

    this.elements_holder = new UIElementHolder(
      this.ctx,
      holder_x,
      0,
      this.total_width,
      holder_h
    );

    let x = 0;
    for ( let i = 0; i < this.ui_elements.length; i++ )
    {
      x += this.getGapAt( i );
      const el = this.ui_elements[i];
      if ( !el )
        continue;

      const el_width = element_widths[i];
      el.getRect().setX( x );

      if ( this.text_element_indices.has( i ) )
      {
        el.getRect().resize( el_width, el.getRect().getH() );
        ( el as UITextElement ).setNaturalTextWidth( false );
      }

      const el_h = el.getRect().getH();
      const el_y = ( holder_h - el_h ) / 2;
      el.getRect().setY( el_y );

      x += el_width;
      this.elements_holder.addChild( el );
    }

    this.addChild( this.elements_holder );
  }

  public recalculate(): void
  {
    if ( this.elements_holder )
    {
      this.removeChild( this.elements_holder );
      this.elements_holder = null;
    }
    this.createAdaptiveElementsByWidth();
  }

  public getTextElement(): UITextElement | undefined
  {
    return this.text_elements[0];
  }

  public getTextElements(): readonly UITextElement[]
  {
    return this.text_elements;
  }

  public isTextElement( index: number ): boolean
  {
    return this.text_element_indices.has( index );
  }

  public getUIElement( index: number ): UIElement | undefined
  {
    return this.ui_elements[index];
  }

  public getTotalWidth(): number
  {
    return this.total_width;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;
    this.startRender();
    this.finishRender();
  }

  public cleanup(): void
  {
    if ( this.recalculate_animation_id != null )
    {
      cancelAnimationFrame( this.recalculate_animation_id );
      this.recalculate_animation_id = null;
    }
    if ( this.onLanguageChange )
    {
      LocalizationManager.unsubscribe( this.onLanguageChange );
      this.onLanguageChange = null;
    }
    super.cleanup();
  }

  public name(): string
  {
    return "UIAdaptiveElementsByTextHorisontal";
  }
}
